import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, UserPlus, UserMinus, Ban, MoreHorizontal, LogOut, Crown, Search, Users2, Trash2 } from 'lucide-react';
import { unifiedChatService } from '@/services/chat/unifiedChatService';
import { teamService } from '@/services/teamService';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { ChatGroupUser } from '@/types/chat';
import { supabase } from '@/integrations/supabase/client';

interface ChatGroupMembersProps {
  chatGroupId: string;
  canManageMembers: boolean;
  currentUserId: string;
  isCreator: boolean;
  chatType: 'open' | 'read_only';
}

interface TeamOption {
  id: number;
  name: string;
  members: Array<{
    id: string;
    username: string;
    first_name?: string;
    last_name?: string;
  }>;
}

const ChatGroupMembers = ({ 
  chatGroupId, 
  canManageMembers, 
  currentUserId, 
  isCreator, 
  chatType 
}: ChatGroupMembersProps) => {
  const [members, setMembers] = useState<ChatGroupUser[]>([]);
  const [searchUsers, setSearchUsers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [addMode, setAddMode] = useState<'individual' | 'team'>('individual');
  const [availableTeams, setAvailableTeams] = useState<TeamOption[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { user } = useUnifiedUser();
  const { profile } = useAuth();
  const { user: internalUser } = useInternalAuth();

  useEffect(() => {
    loadMembers();
    if (isCreator) {
      loadAvailableTeams();
    }
  }, [chatGroupId]);

  const loadMembers = async () => {
    try {
      const groupMembers = await unifiedChatService.getChatGroupUsers(chatGroupId);
      setMembers(groupMembers);
    } catch (error) {
      console.error('Error loading group members:', error);
    }
  };

  const loadAvailableTeams = async () => {
    try {
      console.log('Loading available teams for user:', currentUserId);
      
      // Get user's teams
      const userTeams = await teamService.getUserTeams(currentUserId);
      console.log('User teams found:', userTeams);

      // Convert to TeamOption format and get team members
      const teamsWithMembers = await Promise.all(
        userTeams.map(async (team) => {
          try {
            // Get team players from different tables
            const teamPlayers = await teamService.getTeamPlayers(team.team_id);
            
            const members = teamPlayers.map(player => ({
              id: player.user_id,
              username: `Player-${player.user_id.slice(0, 8)}`,
              first_name: undefined,
              last_name: undefined
            }));

            return {
              id: team.team_id,
              name: team.team_name,
              members
            };
          } catch (error) {
            console.error(`Error loading members for team ${team.team_id}:`, error);
            return {
              id: team.team_id,
              name: team.team_name,
              members: []
            };
          }
        })
      );

      console.log('Teams with members:', teamsWithMembers);
      setAvailableTeams(teamsWithMembers);
    } catch (error) {
      console.error('Error loading teams:', error);
      setAvailableTeams([]);
    }
  };

  const searchAvailableUsers = async (query: string) => {
    console.log('=== SEARCHING USERS ===');
    console.log('Search query:', query);
    
    if (!query.trim()) {
      setSearchUsers([]);
      return;
    }

    try {
      // Clean the query - remove @ symbol if present
      const cleanQuery = query.startsWith('@') ? query.slice(1) : query;
      console.log('Clean query:', cleanQuery);

      // Get all users from both systems
      const allUsers = await unifiedChatService.getAllUsers();
      console.log('All users found:', allUsers.length);
      
      // Get current member IDs to exclude them
      const memberUserIds = new Set(members.map(m => m.user_id));
      console.log('Current member IDs:', Array.from(memberUserIds));
      
      // Filter users based on search query and exclude existing members
      const availableUsers = allUsers.filter(user => {
        // Exclude current members
        if (memberUserIds.has(user.id)) {
          return false;
        }
        
        // Search in username, first_name, and last_name
        const searchText = cleanQuery.toLowerCase();
        const matchesUsername = user.username?.toLowerCase().includes(searchText);
        const matchesFirstName = user.first_name?.toLowerCase().includes(searchText);
        const matchesLastName = user.last_name?.toLowerCase().includes(searchText);
        
        const matches = matchesUsername || matchesFirstName || matchesLastName;
        
        console.log(`User ${user.username}: matches=${matches} (username=${matchesUsername}, firstName=${matchesFirstName}, lastName=${matchesLastName})`);
        
        return matches;
      });
      
      console.log('Filtered available users:', availableUsers);
      setSearchUsers(availableUsers);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchUsers([]);
    }
  };

  const handleAddMember = async (userId: string) => {
    if (!user || !isCreator) {
      toast.error('Only group creators can add members');
      return;
    }

    try {
      setLoading(true);
      await unifiedChatService.addUsersToGroup(chatGroupId, [userId]);
      await loadMembers();
      setSearchQuery('');
      setSearchUsers([]);
      toast.success('Member added successfully');
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error('Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeam = async () => {
    if (!selectedTeam || !isCreator) {
      toast.error('Please select a team to add');
      return;
    }

    try {
      setLoading(true);
      const team = availableTeams.find(t => t.id.toString() === selectedTeam);
      if (!team) {
        toast.error('Team not found');
        return;
      }

      const memberUserIds = new Set(members.map(m => m.user_id));
      const newMemberIds = team.members
        .filter(member => !memberUserIds.has(member.id))
        .map(member => member.id);

      if (newMemberIds.length === 0) {
        toast.info('All team members are already in the group');
        return;
      }

      await unifiedChatService.addUsersToGroup(chatGroupId, newMemberIds);
      await loadMembers();
      setSelectedTeam('');
      toast.success(`Added ${newMemberIds.length} team members to the group`);
    } catch (error) {
      console.error('Error adding team:', error);
      toast.error('Failed to add team members');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId: string, username: string) => {
    if (!user || !isCreator) {
      toast.error('Only group creators can remove members');
      return;
    }

    try {
      setLoading(true);
      await unifiedChatService.leaveGroup(chatGroupId, { id: userId, username, userType: 'supabase', role: 'player' });
      await loadMembers();
      toast.success(`Removed ${username} from the group`);
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockMember = async (userId: string, username: string) => {
    if (!user || !isCreator) {
      toast.error('Only group creators can block members');
      return;
    }

    try {
      setLoading(true);
      await unifiedChatService.leaveGroup(chatGroupId, { id: userId, username, userType: 'supabase', role: 'player' });
      await loadMembers();
      toast.success(`Blocked ${username} from the group`);
    } catch (error) {
      console.error('Error blocking member:', error);
      toast.error('Failed to block member');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!user) return;

    try {
      setLoading(true);
      await unifiedChatService.leaveGroup(chatGroupId, user);
      toast.success('You have left the group');
      window.location.reload();
    } catch (error) {
      console.error('Error leaving group:', error);
      toast.error('Failed to leave group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!user || !isCreator) {
      toast.error('Only group creators can delete the group');
      return;
    }

    try {
      setLoading(true);
      
      // Get group details for notifications
      const { data: group } = await supabase
        .from('chat_groups')
        .select('chat_name')
        .eq('chat_id', chatGroupId)
        .single();

      const groupName = group?.chat_name || 'Unknown Group';

      // Get all group members before deletion
      const { data: groupMembers } = await supabase
        .from('chat_group_users')
        .select('user_id')
        .eq('chat_group_id', chatGroupId)
        .eq('is_active', true);

      // Delete the group
      await unifiedChatService.deleteChatGroup(chatGroupId);

      // Create notifications for all members (except creator)
      const { unifiedNotificationService } = await import('../../services/unifiedNotificationService');
      
      groupMembers?.forEach(member => {
        if (member.user_id !== currentUserId) {
          unifiedNotificationService.createNotification({
            type: 'message',
            chat_group_id: chatGroupId,
            message_id: `group_deleted_${Date.now()}_${member.user_id}`,
            from_user_id: currentUserId,
            to_user_id: member.user_id,
            message_preview: `${groupName} was deleted`,
            chat_group_name: groupName,
            from_username: user.username,
            is_read: false
          });
        }
      });

      toast.success(`Group "${groupName}" has been deleted`);
      
      // Redirect to chat home or refresh the chat list
      window.location.href = '/chat';
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Group Info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-sports-green" />
          <span className="font-semibold text-white">
            Group Members ({members.length})
          </span>
        </div>
        
        {isCreator && (
          <Button
            onClick={() => setShowAddMembers(!showAddMembers)}
            size="sm"
            className="bg-sports-green hover:bg-sports-green/90 text-black"
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Add
          </Button>
        )}
      </div>

      {/* Add Members Section */}
      {showAddMembers && isCreator && (
        <div className="border border-gray-600 rounded-lg p-4 bg-gray-800 space-y-4">
          {/* Add Mode Selector */}
          <div className="flex items-center gap-4">
            <Button
              onClick={() => setAddMode('individual')}
              size="sm"
              variant={addMode === 'individual' ? 'default' : 'outline'}
              className={addMode === 'individual' ? 'bg-sports-green text-black' : 'text-white border-gray-600'}
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Individual
            </Button>
            <Button
              onClick={() => setAddMode('team')}
              size="sm"
              variant={addMode === 'team' ? 'default' : 'outline'}
              className={addMode === 'team' ? 'bg-sports-green text-black' : 'text-white border-gray-600'}
            >
              <Users2 className="h-4 w-4 mr-1" />
              Team
            </Button>
          </div>

          {/* Individual User Search */}
          {addMode === 'individual' && (
            <>
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search users by @username or name..."
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    searchAvailableUsers(value);
                  }}
                  className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                />
              </div>
              
              {/* Search Results */}
              {searchQuery.length > 0 && (
                <ScrollArea className="max-h-32">
                  <div className="space-y-1">
                    {searchUsers.length > 0 ? (
                      searchUsers.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-2 hover:bg-gray-700 rounded"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-sports-green rounded-full flex items-center justify-center">
                              <span className="text-xs font-bold text-black">
                                {user.username?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <span className="text-sm text-white">
                              @{user.username}
                              {(user.first_name || user.last_name) && (
                                <span className="text-gray-400 ml-2">
                                  ({user.first_name} {user.last_name})
                                </span>
                              )}
                            </span>
                          </div>
                          <Button
                            onClick={() => handleAddMember(user.id)}
                            size="sm"
                            variant="ghost"
                            className="text-sports-green hover:bg-sports-green/20"
                            disabled={loading}
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-400">
                        No users found matching "{searchQuery}"
                      </div>
                    )}
                  </div>
                </ScrollArea>
              )}
            </>
          )}

          {/* Team Selection */}
          {addMode === 'team' && (
            <div className="space-y-3">
              <div>
                <label className="text-sm text-gray-300 mb-2 block">Select Team to Add:</label>
                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Choose a team..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {availableTeams.map((team) => (
                      <SelectItem key={team.id} value={team.id.toString()} className="text-white">
                        {team.name} ({team.members.length} members)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedTeam && (
                <Button
                  onClick={handleAddTeam}
                  className="bg-sports-green hover:bg-sports-green/90 text-black"
                  disabled={loading}
                >
                  <Users2 className="h-4 w-4 mr-2" />
                  Add Team to Chat
                </Button>
              )}
              
              {availableTeams.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-400 mb-2">
                    No teams available to add to chat.
                  </p>
                  <Button
                    onClick={loadAvailableTeams}
                    variant="ghost"
                    size="sm"
                    className="text-sports-green hover:bg-sports-green/20"
                  >
                    Refresh Teams
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Members List */}
      <div className="space-y-2">{/* Remove ScrollArea height restriction - let parent dropdown handle scrolling */}
        {members.map((member) => (
          <div
            key={member.user_id}
            className="flex items-center justify-between p-2 bg-gray-800 rounded-lg"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-sports-green rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-black">
                  {member.user_details?.username?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">
                    @{member.user_details?.username || 'Unknown User'}
                  </span>
                  {member.user_details?.first_name && (
                    <span className="text-xs text-gray-400">
                      ({member.user_details.first_name} {member.user_details.last_name})
                    </span>
                  )}
                  {member.user_id === currentUserId && (
                    <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                      You
                    </span>
                  )}
                  {isCreator && member.user_id === currentUserId && (
                    <Crown className="h-4 w-4 text-yellow-500" />
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  Joined {new Date(member.joined_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Member Actions */}
            {member.user_id !== currentUserId && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-gray-800 border-gray-700">
                  {isCreator && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleBlockMember(member.user_id, member.user_details?.username || 'Unknown')}
                        className="text-yellow-400 hover:bg-gray-700"
                        disabled={loading}
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        Block User
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleRemoveMember(member.user_id, member.user_details?.username || 'Unknown')}
                        className="text-red-400 hover:bg-gray-700"
                        disabled={loading}
                      >
                        <UserMinus className="h-4 w-4 mr-2" />
                        Remove from Group
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ))}
      </div>

      {/* Delete Group Button - Only for creators */}
      {isCreator && (
        <div className="border-t border-gray-600 pt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                disabled={loading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Group
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-gray-800 border-gray-700">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Delete Group</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-300">
                  Once deleted it cannot be recovered and all information will be lost. 
                  Are you sure you want to permanently delete this group?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteGroup}
                  className="bg-red-600 hover:bg-red-700"
                  disabled={loading}
                >
                  Delete Group
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Leave Group Button */}
      {!isCreator && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="w-full border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
              disabled={loading}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Leave Group
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-gray-800 border-gray-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Leave Group</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-300">
                Are you sure you want to leave this group? You won't receive any more messages from this group.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleLeaveGroup}
                className="bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                Leave Group
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default ChatGroupMembers;
